import { useMemo, useState, type ReactNode } from 'react';
import {
  useAppendWalaSignature,
  useCreateSignedWalaVersion,
  useCreateUnsignedWalaVersion,
  useWalaVersions,
  type WorkAssignmentListAttachmentUpdateBody,
} from 'components/IAP/hooks/assignmentListHooks';
import { selectUser, useAppSelector } from 'slices/commonSelectors';
import Ics204SignDialog from './Ics204SignDialog';
import Ics204SignatureBoxes from './Ics204SignatureBoxes';
import Ics204VersionsDialog, {
  type WalaVersionRow,
} from './Ics204VersionsDialog';
import Ics204VersionStatusBar from './Ics204VersionStatusBar';
import {
  getSignatureCompletion,
  ICS204_OSC_REVIEW_ROLE,
  ICS204_PREPARED_BY_SIGN_ROLE,
  ICS204_PSC_REVIEW_ROLE,
  parseWalaSignatures,
  type WalaSignature,
} from './ics204SignatureSlots';

export type Ics204VersioningPanelProps = {
  walaId: string | null;
  tipId?: string | null;
  tipSignatures: WalaSignature[];
  tipUpdatedAt?: string | null;
  tipUpdatedByUserName?: string | null;
  isCreatingSignedVersion: boolean;
  viewingVersionId: string | null;
  readOnly?: boolean;
  /** Form body (sections 3–8) rendered between the status bar and Boxes 9–11. */
  children?: ReactNode;
  /** Current edit form values used when signing a new signed version. */
  getSignedVersionPayload?: () => Omit<
    WorkAssignmentListAttachmentUpdateBody,
    'walaId'
  >;
  onStartCreateSignedVersion: () => void;
  onCancelCreateSignedVersion: () => void;
  onSignedVersionCreated: () => void;
  onUnsignedVersionCreated: () => void;
  onViewVersion: (version: WalaVersionRow) => void;
  onViewLatest: () => void;
};

const Ics204VersioningPanel = ({
  walaId,
  tipId,
  tipSignatures,
  tipUpdatedAt,
  tipUpdatedByUserName,
  isCreatingSignedVersion,
  viewingVersionId,
  readOnly = false,
  children,
  getSignedVersionPayload,
  onStartCreateSignedVersion,
  onCancelCreateSignedVersion,
  onSignedVersionCreated,
  onUnsignedVersionCreated,
  onViewVersion,
  onViewLatest,
}: Ics204VersioningPanelProps) => {
  const user = useAppSelector(selectUser);
  const defaultSignName =
    user?.profile_settings?.userName?.trim() || user?.user_name?.trim() || '';
  const { data: versions = [] } = useWalaVersions(walaId ?? '');
  const createSigned = useCreateSignedWalaVersion();
  const createUnsigned = useCreateUnsignedWalaVersion();
  const appendSignature = useAppendWalaSignature();

  const [versionsDialog, setVersionsDialog] = useState<'all' | 'signed' | null>(
    null
  );
  const [signRole, setSignRole] = useState<string | null>(null);

  const viewingVersion = useMemo(
    () =>
      viewingVersionId
        ? versions.find((version) => version.id === viewingVersionId)
        : undefined,
    [viewingVersionId, versions]
  );

  const displaySignatures = useMemo(() => {
    if (!viewingVersion) return tipSignatures;
    return parseWalaSignatures(viewingVersion.signatures);
  }, [tipSignatures, viewingVersion]);

  const displayUpdatedAt = viewingVersion?.updatedAt ?? tipUpdatedAt ?? null;
  const displayUpdatedByUserName =
    viewingVersion?.updatedByUserName ?? tipUpdatedByUserName ?? null;

  const tipCompletion = getSignatureCompletion(tipSignatures);
  const signedCount = versions.filter(
    (version) =>
      getSignatureCompletion(parseWalaSignatures(version.signatures)).status ===
      'signed'
  ).length;

  const createItems = useMemo(() => {
    if (readOnly || viewingVersionId || !walaId) return [];
    const items: {
      key: string;
      label: string;
      onSelect: () => void;
      disabled?: boolean;
    }[] = [];

    if (tipCompletion.status !== 'partially-signed') {
      items.push({
        key: 'signed',
        label: 'Create New Signed Version',
        onSelect: onStartCreateSignedVersion,
      });
    }

    if (
      tipCompletion.status === 'partially-signed' ||
      tipCompletion.status === 'signed'
    ) {
      items.push({
        key: 'unsigned',
        label: 'Create New Draft Version',
        onSelect: () => {
          createUnsigned.mutate(walaId, {
            onSuccess: () => onUnsignedVersionCreated(),
          });
        },
      });
    }

    return items;
  }, [
    createUnsigned,
    onStartCreateSignedVersion,
    onUnsignedVersionCreated,
    readOnly,
    tipCompletion.status,
    viewingVersionId,
    walaId,
  ]);

  if (!walaId) return null;

  const confirmSign = (name: string) => {
    if (!signRole) return;
    if (signRole === ICS204_PREPARED_BY_SIGN_ROLE) {
      const payload = getSignedVersionPayload?.() ?? {};
      createSigned.mutate(
        {
          walaId,
          ...payload,
          preparedBy: {
            name,
            role: ICS204_PREPARED_BY_SIGN_ROLE,
            signedAt: Date.now(),
          },
        },
        {
          onSuccess: () => {
            setSignRole(null);
            onSignedVersionCreated();
          },
        }
      );
      return;
    }

    appendSignature.mutate(
      {
        walaId,
        signature: {
          name,
          role: signRole as
            | typeof ICS204_PSC_REVIEW_ROLE
            | typeof ICS204_OSC_REVIEW_ROLE,
          signedAt: Date.now(),
        },
      },
      {
        onSuccess: () => setSignRole(null),
      }
    );
  };

  return (
    <>
      {/*
        Block wrapper (not table-layout) so the status bar's position:sticky
        can track the planning page scrollport after overflow-x is clipped.
      */}
      <div className="tw:relative tw:min-w-0 tw:w-full">
        <Ics204VersionStatusBar
          signatures={displaySignatures}
          viewingPast={!!viewingVersionId}
          isCreatingSignedVersion={isCreatingSignedVersion}
          historyCount={versions.length}
          signedCount={signedCount}
          createItems={createItems}
          updatedAt={displayUpdatedAt}
          updatedByUserName={displayUpdatedByUserName}
          onOpenHistory={() => setVersionsDialog('all')}
          onOpenSigned={() => setVersionsDialog('signed')}
          onViewLatest={onViewLatest}
          onCancelCreateSigned={onCancelCreateSignedVersion}
          onSignThisVersion={
            isCreatingSignedVersion
              ? () => setSignRole(ICS204_PREPARED_BY_SIGN_ROLE)
              : undefined
          }
        />
        {children}
        <div className="tw:px-3 tw:pt-1">
          <Ics204SignatureBoxes
            signatures={displaySignatures}
            showReviewActions={
              !readOnly &&
              !viewingVersionId &&
              !isCreatingSignedVersion &&
              tipCompletion.status !== 'draft'
            }
            disabled={readOnly}
            onReviewAs={(role) => setSignRole(role)}
          />
        </div>
      </div>
      {versionsDialog ? (
        <Ics204VersionsDialog
          open
          kind={versionsDialog}
          versions={versions as WalaVersionRow[]}
          tipId={tipId}
          onClose={() => setVersionsDialog(null)}
          onView={(version) => {
            setVersionsDialog(null);
            onViewVersion(version);
          }}
        />
      ) : null}
      {signRole ? (
        <Ics204SignDialog
          open
          role={signRole}
          title={
            signRole === ICS204_PREPARED_BY_SIGN_ROLE
              ? 'Sign this version'
              : `Review as ${signRole}`
          }
          defaultName={defaultSignName}
          onClose={() => setSignRole(null)}
          onConfirm={confirmSign}
          isPending={createSigned.isPending || appendSignature.isPending}
        />
      ) : null}
    </>
  );
};

export default Ics204VersioningPanel;
